import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { SlashCommand } from './types';
import { useI18n } from '../../../i18n';

interface SlashCommandMenuProps {
  isOpen: boolean;
  onClose: () => void;
  commands: SlashCommand[];
  filter: string;
  onSelect: (command: SlashCommand) => void;
  /** Whether a project is attached (used to disable commands that require one) */
  hasProject?: boolean;
  /** Whether files are attached */
  hasFiles?: boolean;
}

export const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({
  isOpen,
  onClose,
  commands,
  filter,
  onSelect,
  hasProject,
  hasFiles,
}) => {
  const { t } = useI18n();
  const sc = t.qagentChat.slashCommands;
  // Built-in commands show translated text; saved workflows keep their own name and description
  const builtIn = useCallback(
    (cmd: SlashCommand) => (cmd.workflowId ? undefined : sc.commands[cmd.id]),
    [sc],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredCommands = useMemo(
    // Commands that aren't ready yet are left out rather than shown greyed out
    () => commands.filter(cmd => !cmd.comingSoon).filter(cmd => {
      const f = filter.toLowerCase();
      const names = [cmd.name, builtIn(cmd)?.name ?? ''];
      return cmd.id.toLowerCase().startsWith(f) ||
        names.some(name => name.toLowerCase().split(/\s+/).some(word => word.startsWith(f)));
    }),
    [commands, filter, builtIn],
  );

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filter]);

  // Reset when menu opens
  useEffect(() => {
    if (isOpen) setSelectedIndex(0);
  }, [isOpen]);

  // Click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const isDisabled = useCallback((cmd: SlashCommand) => {
    if (cmd.comingSoon) return true;
    if (cmd.requiresProject && !hasProject) return true;
    if (cmd.requiresFiles && !hasFiles) return true;
    return false;
  }, [hasProject, hasFiles]);

  const getDisabledReason = useCallback((cmd: SlashCommand) => {
    if (cmd.comingSoon) return sc.comingSoon;
    if (cmd.requiresProject && !hasProject) return sc.requiresProject;
    if (cmd.requiresFiles && !hasFiles) return sc.requiresFiles;
    return '';
  }, [hasProject, hasFiles, sc]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen || filteredCommands.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
          break;
        case 'Enter':
          event.preventDefault();
          const cmd = filteredCommands[selectedIndex];
          if (cmd && !isDisabled(cmd)) {
            onSelect(cmd);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    },
    [isOpen, filteredCommands, selectedIndex, onSelect, onClose, isDisabled],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen || filteredCommands.length === 0) return null;

  return (
    <div
      ref={menuRef}
      className="absolute z-50 left-0 right-0"
      style={{ bottom: '100%' }}
    >
      <div className="bg-popover border border-border border-b-0 rounded-t-xl overflow-hidden">
        {/* Items */}
        <div className="py-1">
          {filteredCommands.map((cmd, index) => {
            const disabled = isDisabled(cmd);
            const Icon = cmd.icon;
            return (
              <button
                key={cmd.workflowId ?? cmd.id}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => {
                  if (!disabled) onSelect(cmd);
                }}
                disabled={disabled}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-muted focus:outline-none',
                  selectedIndex === index && 'bg-muted',
                  disabled && 'opacity-40 cursor-not-allowed',
                )}
              >
                <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium">/{cmd.id}</span>
                <span className="text-sm text-muted-foreground">
                  {disabled ? getDisabledReason(cmd) : builtIn(cmd)?.description ?? cmd.description}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SlashCommandMenu;
